import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  MotionProvider,
  Reveal,
  StarField,
  Meteors,
  FloatingOrb,
  CrossingShips,
} from "../Components/motion/space-primitives";
import { posts } from "../data/posts";
import SecurityHighlights from "../Components/rebrand/SecurityHighlights";
import Footer from "../Components/rebrand/Footer";

const PAGE_SIZE = 6;
const FILTER_LIMIT = 8;

// Shared cosmic backdrop (drifting starfield + meteors) used behind the header.
// Pure CSS / compositor-only layers — matches the landing hero, zero per-frame cost.
const SpaceBackdrop = () => (
  <>
    <div className="pointer-events-none absolute inset-0 z-0 overflow-hidden">
      <div
        className="bg-drift absolute inset-y-0 left-0 flex h-full"
        style={{ width: "200%", animationDuration: "90s" }}
      >
        <div className="relative h-full w-1/2 shrink-0">
          <StarField count={70} />
        </div>
        <div className="relative h-full w-1/2 shrink-0">
          <StarField count={70} />
        </div>
      </div>
    </div>
    <Meteors count={4} className="z-0 opacity-70" />
    {/* Cruisers flying across the header, each carrying a supported-chain logo */}
    <CrossingShips count={6} className="z-0" />
    <FloatingOrb
      size={460}
      color="rgba(90, 169, 230, 0.16)"
      style={{ top: -140, right: "6%" }}
    />
    <FloatingOrb
      size={360}
      color="rgba(153, 223, 175, 0.14)"
      style={{ top: 120, left: "-6%" }}
    />
  </>
);

// Tag pills shared by every card variant.
const TagList = ({ tags, accent }) => (
  <div className="flex flex-wrap gap-2">
    {tags.map((tag) => (
      <span
        key={tag}
        className="rounded-full px-3 py-1 text-xs font-semibold"
        style={{ color: accent, backgroundColor: `${accent}1A` }}
      >
        {tag}
      </span>
    ))}
  </div>
);

// Themed cover (per-project brand gradient + logo) reused by every card variant.
const Cover = ({ post, className }) => {
  const theme = post.theme;
  return (
    <div
      className={`relative flex items-center justify-center overflow-hidden ${className}`}
    >
      {theme?.coverGradient ? (
        <div
          className="absolute inset-0"
          style={{ background: theme.coverGradient }}
        />
      ) : (
        <img
          src={post.cover}
          alt={post.title}
          className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
      )}
      {theme?.logo && (
        <img
          src={theme.logo}
          alt={`${post.title} logo`}
          className="relative h-14 w-auto opacity-90 transition-opacity group-hover:opacity-100"
        />
      )}
    </div>
  );
};

// Large horizontal hero card for the most recent assessment (zellic-style featured).
const FeaturedCard = ({ post }) => {
  const accent = post.theme?.accent || "#99DFAF";
  return (
    <Link
      to={`/audit/${post.slug}`}
      className="group grid grid-cols-1 overflow-hidden rounded-3xl border border-white/10 bg-white/[0.03] no-underline backdrop-blur-sm transition-colors hover:border-primary/30 md:grid-cols-2"
    >
      <div className="relative">
        <Cover post={post} className="h-full min-h-[280px]" />
        <span className="absolute left-5 top-5 rounded-full border border-primary/40 bg-space-deep/60 px-3 py-1 text-xs font-semibold text-primary backdrop-blur">
          Featured
        </span>
      </div>
      <div className="flex flex-col gap-4 p-8 md:p-10">
        <TagList tags={post.tags} accent={accent} />
        <h2 className="text-2xl font-bold leading-snug text-white transition-colors group-hover:text-primary md:text-3xl">
          {post.title}
        </h2>
        <p className="flex-1 text-base text-gray-400">{post.excerpt}</p>
        <div className="flex items-center gap-3 text-sm text-gray-500">
          <span>{post.author}</span>
          <span>•</span>
          <span>{post.date}</span>
          <span>•</span>
          <span>{post.readingTime}</span>
        </div>
      </div>
    </Link>
  );
};

// Standard grid card.
const BlogCard = ({ post }) => {
  const accent = post.theme?.accent || "#99DFAF";
  return (
    <Link
      to={`/audit/${post.slug}`}
      className="group flex h-full flex-col overflow-hidden rounded-2xl border border-white/10 bg-white/[0.03] no-underline transition-all hover:-translate-y-1 hover:border-primary/30"
    >
      <Cover post={post} className="h-44" />
      <div className="flex flex-1 flex-col gap-3 p-6">
        <TagList tags={post.tags.slice(0, 2)} accent={accent} />
        <h3 className="text-xl font-bold leading-snug text-white transition-colors group-hover:text-primary">
          {post.title}
        </h3>
        <p className="flex-1 text-sm text-gray-400">{post.excerpt}</p>
        <div className="flex items-center justify-between pt-2 text-xs text-gray-500">
          <span>{post.date}</span>
          <span>{post.readingTime}</span>
        </div>
      </div>
    </Link>
  );
};

const Blog = () => {
  const featured = posts[0];
  const rest = posts.slice(1);

  // Build the category filter from every tag used across the assessments.
  const categories = useMemo(() => {
    const all = new Set();
    posts.forEach((p) => p.tags.forEach((t) => all.add(t)));
    return ["All", ...Array.from(all)];
  }, []);

  const [active, setActive] = useState("All");
  const [visible, setVisible] = useState(PAGE_SIZE);
  const [showAllFilters, setShowAllFilters] = useState(false);

  const visibleCategories = showAllFilters
    ? categories
    : categories.slice(0, FILTER_LIMIT);

  const filtered = useMemo(
    () =>
      active === "All" ? rest : posts.filter((p) => p.tags.includes(active)),
    [active, rest],
  );

  const selectCategory = (cat) => {
    setActive(cat);
    setVisible(PAGE_SIZE);
  };

  const shown = filtered.slice(0, visible);
  const hasMore = visible < filtered.length;

  // Smooth-scroll the hero CTA down to the research listing.
  const scrollToLatest = () => {
    const el = document.getElementById("latest-research");
    if (el) {
      const top = el.getBoundingClientRect().top + window.scrollY - 90;
      window.scrollTo({ top, behavior: "smooth" });
    }
  };

  // Set the page title + meta description for the blog view; restore on unmount.
  useEffect(() => {
    const prevTitle = document.title;
    const metaEl = document.querySelector('meta[name="description"]');
    const prevDesc = metaEl?.getAttribute("content");

    document.title = "Research & Security | Nodefleet";
    metaEl?.setAttribute(
      "content",
      "Security assessments, research, and engineering notes from the team running truly decentralized Web3 infrastructure.",
    );

    return () => {
      document.title = prevTitle;
      if (metaEl && prevDesc != null) metaEl.setAttribute("content", prevDesc);
    };
  }, []);

  return (
    <MotionProvider>
      <div className="relative -mt-24 min-h-screen bg-space">
        {/* Header */}
        <section className="relative flex min-h-screen w-full items-center overflow-hidden bg-space-deep py-32 text-white max-sm:py-28">
          <SpaceBackdrop />
          {/* Spaceship in the corner — same fleet asset as the landing hero */}
          <div className="station-arrive pointer-events-none absolute -right-10 bottom-0 z-0 w-[48%] max-w-[760px] max-lg:w-[60%] max-sm:hidden">
            <img
              src="/assets/setions/hero_setion.png"
              alt="Nodefleet space fleet"
              className="h-auto w-full opacity-70"
            />
          </div>
          <div className="relative z-10 w-full px-8 max-sm:px-6 lg:px-20">
            <Reveal>
              <span className="inline-block rounded-full border border-primary/40 px-4 py-1 text-sm text-primary">
                Research & Security
              </span>
            </Reveal>
            <Reveal
              as="h1"
              delay={0.08}
              className="mt-6 max-w-5xl text-6xl font-extrabold leading-[1.02] tracking-tight max-sm:text-5xl sm:text-7xl lg:text-8xl"
            >
              Web3 security
              <br />
              <span className="text-brand-gradient italic">
                research &amp; assessments
              </span>
            </Reveal>
            <Reveal
              delay={0.16}
              className="mt-6 max-w-xl text-lg text-white/60 max-sm:text-base"
            >
              Security research and assessments for Layer 1s, rollups, bridges
              and DeFi.
            </Reveal>
            <Reveal
              delay={0.24}
              className="mt-9 flex flex-wrap items-center gap-4"
            >
              <button
                type="button"
                onClick={scrollToLatest}
                className="rounded-full bg-primary px-7 py-3 font-semibold text-space-deep shadow-glow transition-colors hover:bg-primary-400"
              >
                Read the research
              </button>
              <Link
                to="/"
                className="rounded-full border border-white/20 px-7 py-3 font-semibold text-white no-underline transition-colors hover:border-white/40 hover:bg-white/5"
              >
                Learn more
              </Link>
            </Reveal>
          </div>
        </section>

        {/* Services · Auditors */}
        <SecurityHighlights />

        {/* Featured */}
        <section className="w-full px-8 pt-16 max-sm:px-6 lg:px-20">
          <Reveal>
            <FeaturedCard post={featured} />
          </Reveal>
        </section>

        {/* Latest research + category filter */}
        <section
          id="latest-research"
          className="w-full px-8 py-16 max-sm:px-6 lg:px-20"
        >
          <h2 className="text-3xl font-bold text-white max-sm:text-2xl">
            Latest <span className="text-brand-gradient italic">research</span>
          </h2>

          <div className="mt-6 flex flex-wrap items-center gap-2">
            {visibleCategories.map((cat) => (
              <button
                key={cat}
                type="button"
                onClick={() => selectCategory(cat)}
                className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
                  active === cat
                    ? "bg-primary text-space-deep"
                    : "border border-white/10 text-gray-300 hover:border-white/30 hover:text-white"
                }`}
              >
                {cat}
              </button>
            ))}
            {categories.length > FILTER_LIMIT && (
              <button
                type="button"
                onClick={() => setShowAllFilters((v) => !v)}
                className="rounded-full px-4 py-1.5 text-sm font-medium text-primary transition-colors hover:text-primary-400"
              >
                {showAllFilters
                  ? "Show less"
                  : `+${categories.length - FILTER_LIMIT} more`}
              </button>
            )}
          </div>

          <div className="mt-10 grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
            {shown.map((post) => (
              <BlogCard key={post.slug} post={post} />
            ))}
          </div>

          {filtered.length === 0 && (
            <p className="mt-10 text-center text-gray-500">
              No articles in this category yet.
            </p>
          )}

          {hasMore && (
            <div className="mt-12 flex justify-center">
              <button
                type="button"
                onClick={() => setVisible((v) => v + PAGE_SIZE)}
                className="rounded-full border border-white/20 px-7 py-3 font-semibold text-white transition-colors hover:border-white/40 hover:bg-white/5"
              >
                Show more articles ↓
              </button>
            </div>
          )}
        </section>

        <Footer />
      </div>
    </MotionProvider>
  );
};

export default Blog;
